class CustomCartDrawer extends HTMLElement {
  constructor() {
    super();

    this.openTrigger = document.querySelector(`#${this.dataset.openTrigger}`);
    this.overlay = this.querySelector("#CustomCartOverlay");
    this.closeButton = this.querySelector("[data-close]");
    this.cartCount = document.querySelector(".custom-cart-drawer__header h2");

    this.lastFreeShippingPercent = null;

    this.handleOpen = this.handleOpen.bind(this);
    this.cartRerender = this.cartRerender.bind(this);
  }

  connectedCallback() {
    this.openTrigger?.addEventListener("click", this.handleOpen);
    this.closeButton?.addEventListener("click", this.closeDrawer.bind(this));
    document.addEventListener("click", (event) => {
      if (event.target === this.overlay) this.closeDrawer();
    });

    document.addEventListener("cart:rerender", this.cartRerender);

    // If the drawer is already in DOM and you want the text correct immediately:
    this.renderFreeShippingText();
  }

  disconnectedCallback() {
    this.openTrigger?.removeEventListener("click", this.handleOpen);
    document.removeEventListener("cart:rerender", this.cartRerender);
  }

  /* -----------------------------
    Currency + Money helpers
  ------------------------------ */

  getCurrencyRate() {
    // Most Shopify multi-currency setups expose this:
    // window.Shopify.currency.rate (1 = shop currency)
    const rate = Number(window.Shopify?.currency?.rate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  formatMoney(cents) {
    const amount = cents / 100;

    const locale = document.documentElement.lang || "en-US";
    const currency = Shopify.currency.active || "USD";

    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency,
    }).format(amount);
  }

  computeFreeShipping() {
    const el = this.querySelector("[data-free-shipping]");
    if (!el) {
      return {
        percent: 0,
        thresholdActiveCents: 0,
        totalCents: 0,
        remainingCents: 0,
        unlocked: false,
      };
    }

    const thresholdShopCents = Number(el.dataset.thresholdShopCents || 0);
    const totalCents = Number(el.dataset.totalCents || 0);
    const rate = this.getCurrencyRate();

    const thresholdActiveCents = Math.round(thresholdShopCents * rate);
    const remainingCents = Math.max(0, thresholdActiveCents - totalCents);

    const percent = thresholdActiveCents
      ? Math.min(100, Math.round((totalCents / thresholdActiveCents) * 100))
      : 0;

    return {
      percent,
      thresholdActiveCents,
      totalCents,
      remainingCents,
      unlocked: thresholdActiveCents > 0 && totalCents >= thresholdActiveCents,
    };
  }

  renderFreeShippingText() {
    const el = this.querySelector("[data-free-shipping]");
    if (!el) return;

    const text = el.querySelector(".cart-free-shipping__text");
    const bar = el.querySelector(".cart-free-shipping__bar");
    if (!text || !bar) return;

    const { unlocked, remainingCents, thresholdActiveCents, totalCents } =
      this.computeFreeShipping();

    text.innerHTML = unlocked
      ? `You’ve unlocked <span>FREE SHIPPING!</span>`
      : `Spend ${this.formatMoney(
          remainingCents,
        )} More To unlock <span>FREE SHIPPING</span>.`;

    bar.setAttribute("aria-valuemax", String(thresholdActiveCents));
    bar.setAttribute("aria-valuenow", String(totalCents));
  }

  getCurrentFreeShippingPercent() {
    // Prefer last known percent (most reliable across DOM swaps),
    // but try to read from fill width if present.
    const el = this.querySelector("[data-free-shipping]");
    if (!el) return null;

    const fill = el.querySelector(".cart-free-shipping__fill");
    if (!fill || !fill.parentElement) return this.lastFreeShippingPercent;

    const fillW = parseFloat(getComputedStyle(fill).width) || 0;
    const parentW = parseFloat(getComputedStyle(fill.parentElement).width) || 0;
    if (!parentW) return this.lastFreeShippingPercent;

    const p = Math.round((fillW / parentW) * 100);
    return Number.isFinite(p) ? p : this.lastFreeShippingPercent;
  }

  animateFreeShippingProgress(fromPercent = null) {
    const el = this.querySelector("[data-free-shipping]");
    if (!el) return;

    // Update the message and aria values first
    this.renderFreeShippingText();

    const { percent: toPercent } = this.computeFreeShipping();
    const fill = el.querySelector(".cart-free-shipping__fill");
    if (!fill) return;

    // pick start percent
    if (fromPercent === null || !Number.isFinite(fromPercent)) {
      fromPercent =
        this.lastFreeShippingPercent !== null
          ? this.lastFreeShippingPercent
          : 0;
    }

    // 1) start instantly (no transition)
    fill.style.transition = "none";
    fill.style.width = `${fromPercent}%`;

    // 2) force layout
    fill.getBoundingClientRect();

    // 3) restore transition explicitly (matches your CSS)
    fill.style.transition = "width 0.55s ease";

    // 4) animate to target on next paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fill.style.width = `${toPercent}%`;
      });
    });

    this.lastFreeShippingPercent = toPercent;
  }

  /* -----------------------------
    Drawer handlers
  ------------------------------ */

  handleOpen(event) {
    event.preventDefault();
    this.openDrawer();
    // animate once on open
    this.animateFreeShippingProgress(this.lastFreeShippingPercent);
  }

  openDrawer() {
    this.setAttribute("open", "");
  }

  closeDrawer() {
    this.removeAttribute("open");
  }

  cartRerender(event) {
    // capture old percent BEFORE DOM replacement
    const prevPercent = this.getCurrentFreeShippingPercent();

    const fakeElement = document.createElement("div");
    const fakeCount = document.createElement("div");
    const newHTML = event.detail.sections["el-custom-cart-drawer"];
    const newCount = event.detail.sections["el-header"];
    fakeElement.innerHTML = newHTML;
    fakeCount.innerHTML = newCount;

    // swap drawer content
    this.querySelector(".custom-cart-drawer__inner").innerHTML =
      fakeElement.querySelector(".custom-cart-drawer__inner").innerHTML;

    document.querySelector(".header__cart-count").innerHTML =
      fakeCount.querySelector(".header__cart-count").innerHTML;

    // update header count
    this.cartCount.innerHTML = fakeElement.querySelector(
      ".custom-cart-drawer__header h2",
    ).innerHTML;

    // animate from previous -> new once
    this.animateFreeShippingProgress(prevPercent);

    // ensure drawer stays open
    this.openDrawer();
  }
}

customElements.define("custom-cart-drawer", CustomCartDrawer);

class AtcButton extends HTMLElement {
  constructor() {
    super();
    this.submitForm = this.querySelector('form[action*="/cart/add"]');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.textButton = this.querySelector("[data-button-text]");
    this.isSubmitting = false;
    this.requiresKachingSelection = this.hasAttribute(
      "data-require-kaching-selection",
    );
    if (this.textButton && !this.textButton.dataset.defaultText) {
      this.textButton.dataset.defaultText = this.textButton.textContent.trim();
    }
  }

  connectedCallback() {
    this.handleSubmit = this.handleSubmit.bind(this);
    this.updateBuyButtonState = this.updateBuyButtonState.bind(this);

    this.submitForm?.addEventListener("submit", this.handleSubmit);

    if (this.requiresKachingSelection) {
      this.observeKachingSelection();
      this.updateBuyButtonState();
    }
  }

  disconnectedCallback() {
    this.submitForm?.removeEventListener("submit", this.handleSubmit);
    this.kachingObserver?.disconnect();
  }

  setLoading(isLoading) {
    const card = this.closest(".el-cart-recommend-card");

    if (card) card.classList.toggle("is-loading", isLoading);
    if (this.submitButton)
      this.submitButton.classList.toggle("is-loading", isLoading);

    if (this.textButton) {
      this.textButton.textContent = isLoading
        ? "Adding..."
        : this.textButton.dataset.defaultText || "ADD TO CART";
      if (this.textButton.tagName === "BUTTON") {
        this.textButton.disabled = isLoading;
      }
    }

    this.isSubmitting = isLoading;
    this.updateBuyButtonState();
  }

  hasKachingSelection() {
    return !!document.querySelector(".kaching-bundles__bar--selected");
  }

  updateBuyButtonState() {
    if (!this.submitButton) return;

    if (this.requiresKachingSelection) {
      const hasSelectedBundle = this.hasKachingSelection();
      this.submitButton.disabled = this.isSubmitting || !hasSelectedBundle;
      return;
    }

    this.submitButton.disabled = this.isSubmitting;
  }

  observeKachingSelection() {
    this.kachingObserver = new MutationObserver(() => {
      this.updateBuyButtonState();
    });

    this.kachingObserver.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    document.addEventListener("click", this.updateBuyButtonState);
  }

  buildPayload() {
    const formData = new FormData(this.submitForm);

    const item = {
      id: formData.get("id"),
      quantity: Number(formData.get("quantity") || 1),
    };

    const sellingPlan = formData.get("selling_plan");
    if (sellingPlan) item.selling_plan = sellingPlan;

    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.*)\]$/);
      if (match) properties[match[1]] = value;
    }

    if (Object.keys(properties).length) {
      item.properties = properties;
    }

    return {
      items: [item],
      sections: "el-custom-cart-drawer,el-header",
    };
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting || !this.submitForm) return;

    if (this.requiresKachingSelection && !this.hasKachingSelection()) {
      this.updateBuyButtonState();
      return;
    }

    this.setLoading(true);

    try {
      const payload = this.buildPayload();

      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) throw data;

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:rerender", {
          detail: data,
          bubbles: true,
        }),
      );
    } catch (error) {
      console.error("Error:", error);
    } finally {
      this.setLoading(false);
    }
  }
}

if (!customElements.get("atc-button")) {
  customElements.define("atc-button", AtcButton);
}

class VariantSelectorSync {
  constructor(container = document) {
    this.container = container;
    this.selectors = this.container.querySelectorAll("[data-variant-selector]");
    this.handleChange = this.handleChange.bind(this);
    this.init();
  }

  init() {
    this.selectors.forEach((select) => {
      select.addEventListener("change", this.handleChange);

      // Sync on load too
      this.syncVariant(select);
    });
  }

  destroy() {
    this.selectors.forEach((select) => {
      select.removeEventListener("change", this.handleChange);
    });
  }

  handleChange(event) {
    const select = event.currentTarget;
    this.syncVariant(select);
  }

  syncVariant(select) {
    const selectedVariantId = select.value;
    const atcButton = select.parentElement?.nextElementSibling;

    if (!atcButton || atcButton.tagName.toLowerCase() !== "atc-button") return;

    const variantInput = atcButton.querySelector('[name="id"]');
    if (!variantInput) return;

    variantInput.value = selectedVariantId;
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new VariantSelectorSync();
});

class CartCheckbox extends HTMLElement {
  constructor() {
    super();
    this.checkbox = this.querySelector('input[type="checkbox"]');
    this.variantId = this.dataset.id;
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.handleChange = this.handleChange.bind(this);
    this.checkbox?.addEventListener("change", this.handleChange);
  }

  disconnectedCallback() {
    this.checkbox?.removeEventListener("change", this.handleChange);
  }

  setLoading(isLoading) {
    this.isSubmitting = isLoading;
    if (this.checkbox) this.checkbox.disabled = isLoading;
    this.classList.toggle("is-loading", isLoading);
  }

  async handleChange(event) {
    if (this.isSubmitting) return;

    const isChecked = event.target.checked;
    this.setLoading(true);

    try {
      const data = isChecked ? await this.addItem() : await this.removeItem();

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:rerender", {
          detail: data,
          bubbles: true,
        }),
      );
    } catch (error) {
      console.error("Cart checkbox error:", error);
      this.checkbox.checked = !isChecked;
    } finally {
      this.setLoading(false);
    }
  }

  async addItem() {
    const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        items: [{ id: this.variantId, quantity: 1 }],
        sections: "el-custom-cart-drawer,el-header",
      }),
    });

    if (!response.ok) throw new Error("Failed to add item");
    return response.json();
  }

  async removeItem() {
    const cart = await fetch(window.Shopify.routes.root + "cart.js").then((r) =>
      r.json(),
    );

    const matchedItem = cart.items.find(
      (item) => String(item.variant_id) === String(this.variantId),
    );

    const response = await fetch(
      window.Shopify.routes.root + "cart/change.js",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: matchedItem.key,
          quantity: 0,
          sections: "el-custom-cart-drawer,el-header",
        }),
      },
    );

    if (!response.ok) throw new Error("Failed to remove item");
    return response.json();
  }
}

customElements.define("cart-checkbox", CartCheckbox);

const CART_SECTIONS = "el-custom-cart-drawer,el-header";

async function cartRequest(endpoint, payload) {
  const response = await fetch(window.Shopify.routes.root + endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const message =
      data?.description || data?.message || `Request failed: ${endpoint}`;
    throw new Error(message);
  }

  return data;
}

function dispatchCartRerender(data) {
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:rerender", {
      detail: data,
      bubbles: true,
    }),
  );
}

class CartActions extends HTMLElement {
  constructor() {
    super();

    this.plusButton = this.querySelector("[data-plus]");
    this.minusButton = this.querySelector("[data-minus]");
    this.handleChange = this.handleChange.bind(this);

    this.isUpdating = false;
  }

  connectedCallback() {
    this.plusButton?.addEventListener("click", this.handleChange);
    this.minusButton?.addEventListener("click", this.handleChange);
  }

  disconnectedCallback() {
    this.plusButton?.removeEventListener("click", this.handleChange);
    this.minusButton?.removeEventListener("click", this.handleChange);
  }

  setLoading(isLoading) {
    const item = this.closest(".custom-cart-drawer__item");
    if (!item) return;

    item.classList.toggle("is-loading", isLoading);

    if (this.plusButton) this.plusButton.disabled = isLoading;
    if (this.minusButton) this.minusButton.disabled = isLoading;

    const variantSelect = item.querySelector("[data-variant-select]");
    if (variantSelect) variantSelect.disabled = isLoading;

    this.isUpdating = isLoading;
  }

  handleChange(event) {
    event.preventDefault();
    if (this.isUpdating) return;

    const nextQty = Number(event.target.dataset.quantity);

    if (Number.isNaN(nextQty) || nextQty < 0) return;

    this.setLoading(true);

    const formData = {
      line: this.dataset.line,
      quantity: nextQty,
      sections: CART_SECTIONS,
    };

    cartRequest("cart/change.js", formData)
      .then((data) => {
        dispatchCartRerender(data);
      })
      .catch((error) => {
        console.error("Error:", error);
        this.setLoading(false);
      });
  }
}

class CartItemVariantSelect extends HTMLElement {
  constructor() {
    super();

    this.select = this.querySelector("[data-variant-select]");
    this.handleVariantChange = this.handleVariantChange.bind(this);

    this.isUpdating = false;
  }

  connectedCallback() {
    this.select?.addEventListener("change", this.handleVariantChange);
  }

  disconnectedCallback() {
    this.select?.removeEventListener("change", this.handleVariantChange);
  }

  get currentVariantId() {
    return String(this.dataset.currentVariantId || "");
  }

  get quantity() {
    return Number(this.dataset.quantity || 1);
  }

  get line() {
    return Number(this.dataset.line || 0);
  }

  get lineKey() {
    return this.dataset.key || "";
  }

  get properties() {
    const propertiesEl = this.querySelector("[data-line-properties]");
    if (!propertiesEl) return {};

    try {
      const parsed = JSON.parse(propertiesEl.textContent.trim() || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn("Could not parse line item properties", error);
      return {};
    }
  }

  get currentSellingPlan() {
    return (
      this.querySelector("[data-current-selling-plan]")?.value?.trim() || ""
    );
  }

  setLoading(isLoading) {
    const item = this.closest(".custom-cart-drawer__item");
    if (!item) return;

    item.classList.toggle("is-loading", isLoading);

    if (this.select) this.select.disabled = isLoading;

    item.querySelectorAll("[data-plus], [data-minus]").forEach((button) => {
      button.disabled = isLoading;
    });

    this.isUpdating = isLoading;
  }

  async handleVariantChange(event) {
    if (this.isUpdating) return;

    const newVariantId = String(event.target.value || "");
    const oldVariantId = this.currentVariantId;

    if (!newVariantId || newVariantId === oldVariantId) return;

    this.setLoading(true);

    try {
      const selectedOption = event.target.selectedOptions?.[0];
      const supportedSellingPlanIds = (
        selectedOption?.dataset.sellingPlanIds || ""
      )
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

      const addPayload = {
        items: [
          {
            id: Number(newVariantId),
            quantity: this.quantity,
          },
        ],
      };

      const properties = this.properties;
      if (Object.keys(properties).length > 0) {
        addPayload.items[0].properties = properties;
      }

      if (
        this.currentSellingPlan &&
        supportedSellingPlanIds.includes(this.currentSellingPlan)
      ) {
        addPayload.items[0].selling_plan = Number(this.currentSellingPlan);
      }

      await cartRequest("cart/add.js", addPayload);

      const removePayload = {
        quantity: 0,
        sections: CART_SECTIONS,
      };

      if (this.lineKey) {
        removePayload.id = String(this.lineKey);
      } else {
        removePayload.line = this.line;
      }

      const data = await cartRequest("cart/change.js", removePayload);

      dispatchCartRerender(data);
    } catch (error) {
      console.error("Error changing variant:", error);

      if (this.select) {
        this.select.value = oldVariantId;
      }

      this.setLoading(false);
    }
  }
}

if (!customElements.get("cart-actions")) {
  customElements.define("cart-actions", CartActions);
}

if (!customElements.get("cart-item-variant-select")) {
  customElements.define("cart-item-variant-select", CartItemVariantSelect);
}
